import { useMemo } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faBoxArchive, faCalendarXmark } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCalendarDays, faCheck, faPencil, faReply, faXmark } from "@performyard/fontawesome/pro-solid-svg-icons";
import type { MeetingModelJson } from "@performyard/service-contracts";

import { config } from "../config";

export type MenuOptionAction = "edit" | "status" | "calendar";

export type MenuOption = {
	id: string;
	label: string;
	action: MenuOptionAction;
	icon: IconDefinition;
};

type UseMeetingOptionsParams = {
	meeting: MeetingModelJson;
	userId: string;
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
};

type UseMeetingOptionsReturn = {
	options: MenuOption[];
	canEdit: boolean;
	canChangeStatus: boolean;
};

/**
 * Hook for generating meeting options based on user permissions and meeting state.
 * @param meeting - The current meeting object
 * @param userId - Current user's ID
 * @param isCalendarIntegrationEnabled - Whether calendar integration is enabled
 * @param hasCalendarEvent - Whether meeting has a calendar event
 * @param isCalendarMismatch - Whether calendar provider mismatches
 */
export const useMeetingOptions = ({
	meeting,
	userId,
	isCalendarIntegrationEnabled,
	hasCalendarEvent,
	isCalendarMismatch,
}: UseMeetingOptionsParams): UseMeetingOptionsReturn => {
	const canChangeStatus = meeting.created?.userId === userId;
	const canEdit = meeting.status === "active" && meeting.created?.userId === userId;
	const isActiveStatus = meeting.status === "active";
	const canComplete = config.statusOptions.includes(meeting.status) && meeting.status !== "completed";

	const options = useMemo((): MenuOption[] => {
		const result: MenuOption[] = [];

		if (canEdit) {
			result.push({ id: "edit", label: "Edit Meeting", action: "edit", icon: faPencil });
		}

		if (isCalendarIntegrationEnabled && canEdit) {
			if (hasCalendarEvent && !isCalendarMismatch) {
				result.push({
					id: "remove-from-calendar",
					label: "Remove from Calendar",
					action: "calendar",
					icon: faCalendarXmark,
				});
			} else {
				result.push({
					id: "add-to-calendar",
					label: "Add to Calendar",
					action: "calendar",
					icon: faCalendarDays,
				});
			}
		}

		if (canChangeStatus) {
			if (!isActiveStatus) {
				result.push({ id: "active", label: "Reopen Meeting", action: "status", icon: faReply });
			}

			if (canComplete) {
				result.push({ id: "completed", label: "Complete Meeting", action: "status", icon: faCheck });
			}

			if (meeting.status !== "archived") {
				result.push({ id: "archived", label: "Archive Meeting", action: "status", icon: faBoxArchive });
			}

			if (isActiveStatus) {
				result.push({ id: "canceled", label: "Cancel Meeting", action: "status", icon: faXmark });
			}
		}

		return result;
	}, [
		meeting.status,
		canEdit,
		canChangeStatus,
		isCalendarIntegrationEnabled,
		hasCalendarEvent,
		isCalendarMismatch,
		isActiveStatus,
		canComplete,
	]);

	return {
		options,
		canEdit,
		canChangeStatus,
	};
};
